import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  Chip,
  Pagination,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { contractorAPI } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import PortalLayout from '../../components/PortalLayout';

const ContractorBrowseJobsPage: React.FC = () => {
  const [jobs, setJobs] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [direction, setDirection] = useState('');
  const { addToast } = useToast();
  const navigate = useNavigate();

  const loadJobs = useCallback(async () => {
    setIsLoading(true);
    try {
      const params: any = { page, limit: 30 };
      if (direction) params.direction = direction;
      const response = await contractorAPI.getAvailableJobs(params);
      setJobs(response.data.data);
      setTotalPages(response.data.last_page);
      setTotal(response.data.total);
    } catch {
      addToast('Fehler beim Laden der Ausschreibungen', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [page, direction, addToast]);

  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  const formatDate = (d: string) =>
    new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });

  const getDirectionLabel = (dir: string) => {
    if (dir === 'inbound') return 'Inbound';
    if (dir === 'outbound') return 'Outbound';
    if (dir === 'both') return 'Inbound & Outbound';
    return dir || '-';
  };

  const getKontaktStatus = (job: any) => {
    if (job.already_purchased) {
      return <Chip label="Ja" size="small" color="success" variant="outlined" sx={{ fontSize: '0.7rem' }} />;
    }
    if (job.sold_out) {
      return <Typography variant="body2" sx={{ color: '#999', fontStyle: 'italic' }}>Ausverkauft</Typography>;
    }
    return <Typography variant="body2" sx={{ color: '#c62828', fontWeight: 600 }}>Nein</Typography>;
  };

  return (
    <PortalLayout>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 0.5 }}>
            Ausschreibungen
          </Typography>
          <Typography color="textSecondary" variant="body2">
            {total} Ausschreibungen verfügbar
          </Typography>
        </Box>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Telefonie-Art</InputLabel>
          <Select value={direction} onChange={e => { setDirection(e.target.value); setPage(1); }} label="Telefonie-Art">
            <MenuItem value="">Alle</MenuItem>
            <MenuItem value="inbound">Inbound</MenuItem>
            <MenuItem value="outbound">Outbound / Rebound</MenuItem>
            <MenuItem value="both">Inbound & Outbound</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {isLoading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : jobs.length === 0 ? (
        <Paper elevation={0} sx={{ p: 4, textAlign: 'center', border: '1px solid #e0e0e0' }}>
          <Typography color="textSecondary">Keine Ausschreibungen verfügbar.</Typography>
        </Paper>
      ) : (
        <>
          <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0', mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f6fa' }}>
                  <TableCell sx={{ fontWeight: 700, color: '#555', width: 70 }}>Nr.</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#555' }}>Erstellt am</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#555' }}>Kontaktdaten erworben</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#555' }}>Firma</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#555' }}>PLZ</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#555' }}>Stadt</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#555' }}>Inbound/Outbound</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#555' }} align="right">Ansehen</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {jobs.map((job, index) => (
                  <TableRow
                    key={job.id}
                    hover
                    sx={{
                      '&:nth-of-type(even)': { backgroundColor: '#fafafa' },
                      cursor: 'pointer',
                    }}
                  >
                    <TableCell sx={{ fontWeight: 600, color: '#1a1a2e' }}>
                      #{job.id}
                    </TableCell>
                    <TableCell sx={{ color: '#555', fontSize: '0.85rem' }}>
                      {formatDate(job.created_at)}
                    </TableCell>
                    <TableCell>
                      {getKontaktStatus(job)}
                    </TableCell>
                    <TableCell sx={{ color: job.already_purchased ? '#1a1a2e' : '#999', fontStyle: job.already_purchased ? 'normal' : 'italic', fontSize: '0.85rem' }}>
                      {job.firm_masked}
                    </TableCell>
                    <TableCell sx={{ color: '#555', fontSize: '0.85rem' }}>
                      {job.zip_code_masked || 'XXX'}
                    </TableCell>
                    <TableCell sx={{ color: job.already_purchased ? '#1a1a2e' : '#999', fontStyle: job.already_purchased ? 'normal' : 'italic', fontSize: '0.85rem' }}>
                      {job.city_masked}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getDirectionLabel(job.direction)}
                        size="small"
                        variant="outlined"
                        sx={{
                          fontSize: '0.72rem',
                          borderColor: job.direction === 'inbound' ? '#E85D26' : '#533483',
                          color: job.direction === 'inbound' ? '#E85D26' : '#533483',
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => navigate(`/contractor/inquiry/${job.id}`)}
                        sx={{ color: '#E85D26', fontWeight: 600, fontSize: '0.8rem' }}
                      >
                        Datei anzeigen
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box display="flex" justifyContent="center">
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, value) => setPage(value)}
              color="primary"
            />
          </Box>
        </>
      )}
    </PortalLayout>
  );
};

export default ContractorBrowseJobsPage;
